// @ts-strict-ignore
import { getServerSideTranslations } from '@lib/get-server-side-translations'
import { SingleItemResponse, getPageByUrlSlug } from '@lib/kontent'
import { BrandPage as BrandPageModel } from '@lib/kontent/models/BrandPage'
import { CategoryPage as CategoryPageModel } from '@lib/kontent/models/CategoryPage'
import { ContentPage as ContentPageModel } from '@lib/kontent/models/ContentPage'
import { DigitalProductPage as DigitalProductModel } from '@lib/kontent/models/DigitalProductPage'
import { ExclusiveCategoryPage as ExclusiveCategoryPageModel } from '@lib/kontent/models/ExclusiveCategoryPage'
import { RichExperiencePage as RichExperiencePageModel } from '@lib/kontent/models/RichExperiencePage'
import { ThemePage as ThemePageModel } from '@lib/kontent/models/ThemePage'
import { GetStaticPaths, GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import { FC, Fragment, useEffect } from 'react'
import { useEmarsysTracking } from '@lib/emarsys-tracking/use-emarsys-tracking'
import { useEmarsysScriptIsLoaded, useEmarsysScarabQueueIsLoaded } from '@lib/use-emarsys-store'

type Props = React.ReactNode &
  (
    | SingleItemResponse<CategoryPageModel>
    | SingleItemResponse<BrandPageModel>
    | SingleItemResponse<ContentPageModel>
    | SingleItemResponse<DigitalProductModel>
    | SingleItemResponse<ThemePageModel>
    | SingleItemResponse<ExclusiveCategoryPageModel>
    | SingleItemResponse<RichExperiencePageModel>
  )

const BrandPage = dynamic(() => import('../components/_brand-page'))
const CategoryPage = dynamic(() => import('../components/_category-page'))
const ContentPage = dynamic(() => import('../components/_content-page/ContentPage'))
const DigitalPage = dynamic(() => import('../components/_digital-products/page'))
const ThemePage = dynamic(() => import('../components/_theme-page'))
const ExclusiveCategory = dynamic(() => import('../components/_exclusive-category-page'))
const RichExperiencePage = dynamic(() => import('../components/_rich-experience-page'))
const ErrorPage = dynamic(() => import('../components/Error'))

const EXCLUDED_PAGE = ['rolex']

export const Slug: FC<Props> = ({ item, modular_content }) => {
  const { isTrackingReady, setDefaultSettings, go, setCustomPageVisit } = useEmarsysTracking()
  const scriptIsLoaded = useEmarsysScriptIsLoaded()
  const scarabQueueIsLoaded = useEmarsysScarabQueueIsLoaded()

  const props = {
    item,
    modular_content,
  }
  let Comp = null
  switch (item.system.type) {
    case 'brand_page':
      Comp = BrandPage
      break
    case 'category_page':
      Comp = CategoryPage
      break
    case 'content_page':
      Comp = ContentPage
      break
    case 'digital_product_page':
      Comp = DigitalPage
      break
    case 'theme_page':
      Comp = ThemePage
      break
    case 'exclusive_category_page':
      Comp = ExclusiveCategory
      break
    case 'rich_experience_page':
      Comp = RichExperiencePage
      break
    default:
      Comp = ErrorPage
  }

  useEffect(() => {
    if (
      scriptIsLoaded &&
      scarabQueueIsLoaded &&
      isTrackingReady &&
      !['brand_page', 'category_page', 'exclusive_category_page', 'theme_page'].includes(
        item.system.type,
      )
    ) {
      setDefaultSettings()

      const customEmarsysTrackingPageName =
        'emarsys_tracking__name' in item.elements
          ? (item.elements.emarsys_tracking__name as { value?: string })?.value
          : undefined

      if (customEmarsysTrackingPageName) {
        setCustomPageVisit(customEmarsysTrackingPageName)
        go(['view'])
      } else {
        go()
      }
    }
  }, [scriptIsLoaded, scarabQueueIsLoaded, isTrackingReady]) // eslint-disable-line

  return <Fragment>{Comp && <Comp {...props} />}</Fragment>
}

export default Slug

export const getStaticProps: GetStaticProps = async ({ locale, params, preview }) => {
  const urlSlug = typeof params.slug === 'string' ? params.slug : params.slug.join('/')

  if (EXCLUDED_PAGE.includes(urlSlug)) {
    return {
      notFound: true,
    }
  }

  const url = urlSlug.split('/')
  const content = await getPageByUrlSlug<
    | BrandPageModel
    | CategoryPageModel
    | ContentPageModel
    | DigitalProductModel
    | ThemePageModel
    | ExclusiveCategoryPageModel
    | RichExperiencePageModel
  >({
    language: locale,
    urlSlug: url.join('/') as string,
    preview,
  })

  if (!content) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...content,
      ...(await getServerSideTranslations(locale)),
    },
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' }
}
